/**
 * EtOHStudioPublisher — publish a generated visual to EtOH Studio (ContentStudio.io).
 *
 * Flow:
 *  1. (optional) Select an event (auto-suggest a caption from event title + date)
 *  2. Verify / edit the post text
 *  3. Select a content category (default loaded from admin settings)
 *  4. Choose publish mode:
 *      - Queue (slot suivant dans la content category)
 *      - Scheduled (date/heure précise)
 *      - Publish now
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Layers, Calendar as CalIcon, Clock, Zap } from "lucide-react";

const STORE_KEY = "etoh_studio.last";

const PLATFORM_DOT = {
  facebook: "bg-blue-500", instagram: "bg-pink-500", linkedin: "bg-sky-700",
  twitter: "bg-neutral-900", pinterest: "bg-red-600", tiktok: "bg-neutral-900", youtube: "bg-red-600",
};

const MODES = [
  { id: "queued", label: "File d'attente", icon: Layers, hint: "Slot suivant dans la content category" },
  { id: "scheduled", label: "Date précise", icon: CalIcon, hint: "Programmer à une date/heure exacte" },
  { id: "now", label: "Publier", icon: Zap, hint: "Publication immédiate (selon comptes)" },
];

function buildSuggestedCaption(event) {
  if (!event) return "";
  const title = event.title || "";
  const date = event.date ? new Date(event.date).toLocaleDateString("fr", { day: "numeric", month: "long" }) : "";
  const lieu = event.location || event.city || "";
  return `🍷 ${title}\n${date ? `📅 ${date}\n` : ""}${lieu ? `📍 ${lieu}\n` : ""}\nRejoignez-nous → https://www.lawinetech.com/events/${event.slug || ""}\n\n#LaWineTech #wine #innovation`;
}

export default function EtOHStudioPublisher({ imageUrl, defaultText = "", lockedEvent = null }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(lockedEvent?.id || "");
  const [text, setText] = useState(defaultText);
  const [firstComment, setFirstComment] = useState("");
  const [mode, setMode] = useState("queued");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const bootedRef = useRef(false);

  const loadCategories = (ws, preferredCategoryId = "") => {
    setCategoryId("");
    setCategories([]);
    if (!ws) return;
    setLoadingCats(true);
    api.get(`/admin/contentstudio/workspaces/${ws}/content-categories`).then((r) => {
      const items = (r.data?.items || []).filter((c) => (c.accounts || []).length > 0);
      setCategories(items);
      const last = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
      const chosen = preferredCategoryId && items.find((c) => c.id === preferredCategoryId)
        ? preferredCategoryId
        : (last.workspaceId === ws && items.find((c) => c.id === last.categoryId) ? last.categoryId : items[0]?.id || "");
      setCategoryId(chosen);
    }).catch((e) => toast.error(formatApiErrorDetail(e) || "Catégories indisponibles"))
      .finally(() => setLoadingCats(false));
  };

  // Bootstrap: workspaces + defaults + events list
  useEffect(() => {
    if (bootedRef.current) return undefined;
    bootedRef.current = true;
    Promise.all([
      api.get("/admin/contentstudio/workspaces").catch(() => ({ data: { items: [] } })),
      api.get("/admin/contentstudio/defaults").catch(() => ({ data: {} })),
      lockedEvent ? Promise.resolve({ data: [] }) : api.get("/events?limit=50").catch(() => ({ data: [] })),
    ]).then(([wsR, defR, evR]) => {
      const items = wsR.data?.items || [];
      setWorkspaces(items);
      setEvents(Array.isArray(evR.data) ? evR.data : (evR.data?.items || []));
      const last = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
      const fallbackWs = defR.data?.workspace_id || last.workspaceId || items[0]?.id;
      if (fallbackWs && items.find((w) => w.id === fallbackWs)) {
        setWorkspaceId(fallbackWs);
        loadCategories(fallbackWs, defR.data?.content_category_id || "");
      } else if (items[0]?.id) {
        setWorkspaceId(items[0].id);
        loadCategories(items[0].id, "");
      }
    });
    return undefined;
  }, [lockedEvent]);

  // When user selects an event, regenerate the suggested caption
  const selectEvent = (eid) => {
    setEventId(eid);
    if (!eid) return;
    const ev = events.find((e) => e.id === eid) || (lockedEvent?.id === eid ? lockedEvent : null);
    if (ev) setText(buildSuggestedCaption(ev));
  };

  const selectedCategory = categories.find((c) => c.id === categoryId);

  const submit = async () => {
    if (!workspaceId || !categoryId || !text.trim() || !imageUrl) {
      toast.error("Workspace, catégorie, texte et visuel requis.");
      return;
    }
    if (mode === "scheduled" && !scheduledAt) {
      toast.error("Date de publication requise pour le mode programmé.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/contentstudio/publish", {
        workspace_id: workspaceId,
        content_category_id: categoryId,
        text: text.trim(),
        image_url: imageUrl,
        first_comment: firstComment.trim() || undefined,
        publish_mode: mode,
        scheduled_at: mode === "scheduled" ? new Date(scheduledAt).toISOString() : undefined,
      });
      localStorage.setItem(STORE_KEY, JSON.stringify({ workspaceId, categoryId }));
      const label = mode === "now" ? "publié" : mode === "scheduled" ? `programmé pour ${new Date(scheduledAt).toLocaleString("fr")}` : `en file d'attente dans « ${r.data.category} »`;
      toast.success(`✅ Post ${label} (${r.data.accounts_count} compte(s))`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Échec publication EtOH Studio");
    } finally {
      setBusy(false);
    }
  };

  const minDateTime = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-4" data-testid="etoh-publisher">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-wine-600" />
        <h3 className="text-sm font-bold text-navy">Publier sur EtOH Studio</h3>
      </div>

      <label className="mt-3 block text-[10px] font-bold uppercase text-neutral-500">Code client (workspace)</label>
      <select value={workspaceId} onChange={(e) => { setWorkspaceId(e.target.value); loadCategories(e.target.value); }} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="etoh-workspace">
        {workspaces.length === 0 && <option value="">Chargement…</option>}
        {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>

      {!lockedEvent && events.length > 0 && (
        <>
          <label className="mt-3 block text-[10px] font-bold uppercase text-neutral-500">Événement (suggère un texte)</label>
          <select value={eventId} onChange={(e) => selectEvent(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="etoh-event">
            <option value="">— Aucun (texte libre) —</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title} {ev.date ? `· ${new Date(ev.date).toLocaleDateString("fr")}` : ""}</option>)}
          </select>
        </>
      )}

      <label className="mt-3 block text-[10px] font-bold uppercase text-neutral-500">Content category</label>
      <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" disabled={loadingCats} data-testid="etoh-category">
        {loadingCats && <option value="">Chargement…</option>}
        {!loadingCats && categories.length === 0 && <option value="">Aucune catégorie avec compte rattaché</option>}
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.accounts.length} compte(s) · {c.slots_count} slot(s)</option>)}
      </select>
      {selectedCategory && (
        <div className="mt-1.5 flex flex-wrap gap-1" data-testid="etoh-category-accounts">
          {selectedCategory.accounts.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-semibold text-neutral-700">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${PLATFORM_DOT[a.platform] || "bg-neutral-400"}`} />
              {a.name || a.platform}
            </span>
          ))}
        </div>
      )}

      <label className="mt-3 block text-[10px] font-bold uppercase text-neutral-500">Texte du post (vérifier / modifier)</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="Légende du post (hashtags inclus)" data-testid="etoh-text" />
      <div className="mt-0.5 flex items-center justify-between text-[10px] text-neutral-400">
        <span>{text.length} caractères</span>
        {eventId && <button onClick={() => selectEvent(eventId)} className="font-semibold text-wine-600 hover:text-wine-700">↺ Re-suggérer depuis l&apos;événement</button>}
      </div>

      <label className="mt-2 block text-[10px] font-bold uppercase text-neutral-500">Premier commentaire (optionnel)</label>
      <input value={firstComment} onChange={(e) => setFirstComment(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="Ex: Lien d'inscription en bio 👆" data-testid="etoh-first-comment" />

      <label className="mt-3 block text-[10px] font-bold uppercase text-neutral-500">Mode de publication</label>
      <div className="mt-1 grid grid-cols-3 gap-2">
        {MODES.map((m) => (
          <button key={m.id} type="button" onClick={() => setMode(m.id)}
            className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all ${mode === m.id ? "border-wine-500 bg-wine-50 text-wine-700 shadow-sm" : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"}`}
            data-testid={`etoh-mode-${m.id}`} title={m.hint}>
            <m.icon className="h-4 w-4" /> {m.label}
          </button>
        ))}
      </div>
      {mode === "scheduled" && (
        <div className="mt-2">
          <input type="datetime-local" value={scheduledAt} min={minDateTime} onChange={(e) => setScheduledAt(e.target.value)} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="etoh-scheduled-at" />
          <p className="mt-1 text-[10px] text-neutral-500"><Clock className="mr-1 inline h-3 w-3" /> Fuseau : navigateur local</p>
        </div>
      )}

      <button onClick={submit} disabled={busy || !imageUrl || !categoryId} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:cursor-not-allowed disabled:opacity-50" data-testid="etoh-submit">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Envoi…</> : <><Send className="h-4 w-4" />{mode === "now" ? "Publier maintenant" : mode === "scheduled" ? "Programmer" : "Ajouter en file d'attente"}</>}
      </button>
    </div>
  );
}
